<script>
import GanttAside from '@/components/gantt/GanttAside.vue';

import projectApi from '@/api/project';
import stageApi from '@/api/stage';
import taskApi from '@/api/task';
import taskBindApi from '@/api/taskBind';

import DefaultLayout from '@/layouts/Default.vue';
import CommonSection from '@/components/CommonSection.vue';
import BaseSelect from '@/components/BaseSelect.vue';
import BaseSelectMulti from '@/components/BaseSelectMulti.vue';
import GanttChart from '@/components/gantt/GanttChart.vue';

import Access from '@/utils/access';
import modelCustomSettings from '@/model/userCustomSettings';
import BaseCheckbox from '@/components/BaseCheckbox.vue';
import PageSlug from '@/components/PageSlug.vue';

import PushBtn from '@/components/push/PushBtn.vue';

export default {
  name: 'GanttPage',
  components: {
    BaseCheckbox,
    GanttChart,
    GanttAside,
    DefaultLayout,
    CommonSection,
    BaseSelect,
    BaseSelectMulti,
    PageSlug,
  },
  data() {
    return {
      data: [],
      showStages: true,
      filters: {
        manager: null,
        watcher: null,
        member: null,
        status: null,
        priority: null,
        hideClosed: false,
      },
      sort: 'dateStart',
      scale: 'week',
      selectedProjects: [],
      projects: [],
      stages: [],
      tasks: [],
      taskBinds: [],
      members: [],
    };
  },
  computed: {
    currUser() { return this.$store.state.auth.user; },
    foldedStages() { return this.currUser.customSettings.foldedStages ?? []; },
    collapsedProjects() { return this.currUser.customSettings.ganttCollapsedProjects ?? []; },
    // stageSort() { return this.currUser.customSettings.stageSort ?? {}; },
    membersOptions() {
      const nonUnique = this.projects
        .filter(p => this.selectedProjects.includes(p['@id']))
        .reduce((acc, val) => [
          ...acc,
          val.author,
          ...(val?.projectMembers ?? [])
            .map(m => m.user),
        ], []);

      return Object.values(nonUnique.reduce((acc, val) => ({ ...acc, [val['@id']]: val }), {}))
        .map(u => ({ value: u['@id'], label: `${u.name} ${u.surname}` }));
    },
    priorityOptions() { return new Array(10).fill(0).map((v, i) => i + 1); },
    statusOptions() {
      const allStatuses = Object.fromEntries(
        this.projects
          .reduce((acc, val) => [...acc, ...val.taskStatuses], [])
          .map(s => [s['@id'], s.name]),
      );

      return [...new Set(this.tasks.map(t => allStatuses[t.status] ?? 'Без статуса'))];
    },
    tasksByStage() {
      const projectsById = Object.fromEntries(this.projects.map(p => [p['@id'], p]));
      const projectsByStage = Object.fromEntries(this.stages.map(s => [s.id, projectsById?.[s.project]]));

      const isTaskEditable = t => {
        const access = new Access(
          { ...t, project: projectsByStage?.[t.stage.id] },
          this.$store.state.auth.user,
        );
        try {
          return access.canUpdateTask?.(true);
        } catch (e) {
          return false;
        }
      };

      return this.tasks
        .map(task => {
          const project = projectsByStage?.[task.stage.id] ?? {};
          const status = (project.taskStatuses ?? []).find(s => s['@id'] === task.status)?.name ?? 'Без статуса';

          return { ...task, status };
        })
        .filter(this.taskFilter)
        .map(task => ({
          isEditable: isTaskEditable(task),
          ...task,
          ...this.initDates(task),
        }))
        .sort(this.taskSort)
        .reduce((acc, task) => {
          const stageId = task.stage['@id'];

          acc[stageId] ??= [];
          acc[stageId].push(task);
          return acc;
        }, {});
    },
    stagesByProject() {
      return this.stages.reduce((acc, stage) => {
        const projectId = stage.project;

        const stageTasks = (this.tasksByStage[stage['@id']] ?? []);// .sort((a, b) => a.dateStart - b.dateStart);
        const stageDates = stageTasks.reduce((a, val) => [...a, val.dateStart, val.dateFinish], []);
        const isEditable = stageTasks.reduce((a, val) => a || val.isEditable, false);

        if (stageTasks.length) {
          acc[projectId] ??= [];
          acc[projectId].push({
            id: stage.id,
            '@id': stage['@id'],
            '@type': stage['@type'],
            name: stage.name,
            isEditable,
            children: stageTasks,
            collapsed: this.foldedStages.includes(stage.id),
            dateStart: new Date(Math.min(...stageDates)),
            dateFinish: new Date(Math.max(...stageDates)),
          });
        }

        return acc;
      }, {});
    },
    dataCombined() {
      return this.projects.map(project => {
        const stages = this.stagesByProject[project['@id']] ?? [];
        const areAllStagesFolded = !stages.find(stage => !this.foldedStages.includes(stage.id));

        const authorSort = project?.authorStageSort;
        const userSort = this.currUser.customSettings?.stageSort?.[project.id];
        const sort = userSort ?? authorSort ?? {};

        const collapsed = this.collapsedProjects.includes(project.id);
        const children = this.showStages
          ? stages.sort((a, b) => (sort[a.id] ?? 0) - (sort[b.id] ?? 0) || b.id - a.id)
          : stages.reduce((acc, val) => [...acc, ...val.children], []).sort(this.taskSort);

        return {
          ...project,
          ...this.initDates(project),
          isEditable: (new Access(project, this.currUser)).canEditProject(),
          members: project.projectMembers.map(m => m.user),
          areAllStagesFolded,
          children,
          collapsed,
        };
      });
    },
    dataFiltered() {
      return this.dataCombined
        .filter(p => this.selectedProjects.includes(p['@id']) && p.children.length);
    },
  },
  methods: {
    taskSort(a, b) {
      if (a.isClosed !== b.isClosed) {
        return a.isClosed - b.isClosed;
      }

      switch (this.sort) {
        case 'dateStart': return a.dateStart - b.dateStart;
        case 'priority': return b.priority - a.priority;
        case 'status': return a.name.localeCompare(b.name);
        default: return 0;
      }
    },
    taskFilter(t) {
      return (!this.filters.hideClosed || !t.isClosed) // eslint-disable-line
        && (this.filters.status === null || this.filters.status === t.status) // eslint-disable-line
        && (this.filters.priority === null || this.filters.priority === t.priority) // eslint-disable-line
        && (this.filters.manager === null || this.filters.manager === t.manager?.['@id']) // eslint-disable-line
        && (this.filters.watcher === null || !!t.members.find(m => m['@id'] === this.filters.watcher)) // eslint-disable-line
        && (
          this.filters.member === null
          || !!t.members.find(m => m['@id'] === this.filters.member)
          || this.filters.member === t.manager?.['@id']
          || this.filters.member === t.author?.['@id']
        ); // eslint-disable-line
    },
    toggleProjectFolding(id) {
      const ganttCollapsedProjects = this.collapsedProjects.includes(id)
        ? this.collapsedProjects.filter(p => p !== id)
        : [id, ...this.collapsedProjects];

      modelCustomSettings.set({ ganttCollapsedProjects });
    },
    toggleStageFoldingByProject({ id, expand }) {
      const stageIds = this.dataFiltered.find(p => p.id === id).children.map(s => s.id);

      let foldedStages = this.foldedStages.filter(i => stageIds.indexOf(i) === -1);

      if (!expand) {
        foldedStages = [...foldedStages, ...stageIds];
      }

      modelCustomSettings.set({ foldedStages });
    },
    update(itemsToUpdate) {
      const cancelData = [];
      const apiCalls = [];
      itemsToUpdate.forEach(({ item, dateStart, dateFinish }) => {
        const [collection, api] = item['@type'] === 'Project' ? [this.projects, projectApi] : [this.tasks, taskApi];
        const updatable = collection.find(i => i['@id'] === item['@id']);

        cancelData.push({
          api,
          updatable,
          dateStart: new Date(updatable.dateStart),
          dateFinish: new Date(updatable.dateFinish),
        });

        updatable.dateStart = new Date(dateStart);
        updatable.dateFinish = new Date(dateFinish);

        apiCalls.push(api.update(updatable.id, { dateStart, dateFinish }));
      });

      Promise.all(apiCalls)
        .then(() => {
          this.pushCancel(() => {
            // eslint-disable-next-line
            cancelData.forEach(({ api, updatable, dateStart, dateFinish }) => {
              updatable.dateStart = dateStart; // eslint-disable-line
              updatable.dateFinish = dateFinish;  // eslint-disable-line
              api.update(updatable.id, { dateStart, dateFinish });
            });
          });
        })
        .catch(() => {
          cancelData.forEach(({ updatable, dateStart, dateFinish }) => {
            updatable.dateStart = dateStart; // eslint-disable-line
            updatable.dateFinish = dateFinish;  // eslint-disable-line
          });
        });
    },
    pushCancel(handler) {
      this.$push({
        component: {
          is: PushBtn,
          props: { label: 'Отменить', title: 'Данные сохранены' },
          emits: { click: handler },
        },
      });
    },
    initDates(item) {
      let dateStart = item.dateStart ? new Date(item.dateStart) : null;
      let dateFinish = item.dateFinish ? new Date(item.dateFinish) : null;
      const now = new Date();

      if (dateStart && !dateFinish) {
        dateFinish = dateStart > now ? new Date(dateStart) : new Date();
      } else if (!dateStart && dateFinish) {
        dateStart = dateFinish < now ? new Date(dateFinish) : new Date();
      } else if (!dateStart && !dateFinish) {
        dateStart = new Date();
        dateFinish = new Date();
      }

      return { dateStart, dateFinish };
    },
    toggleStageFolding(id) {
      const foldedStages = this.foldedStages.includes(id)
        ? this.foldedStages.filter(i => i !== id)
        : [...this.foldedStages, id];

      modelCustomSettings.set({ foldedStages });
    },
    bind({ from, to, isStart }) {
      const newItem = { from, to, isStart, id: null }; // eslint-disable-line
      this.taskBinds.push(newItem);

      taskBindApi.create(newItem)
        .then(r => {
          newItem.id = r.id;
          this.pushCancel(() => {
            this.taskBinds = this.taskBinds.filter(b => b.id !== newItem.id);
            taskBindApi.delete(newItem.id);
          });
        })
        .catch(() => { this.taskBinds = this.taskBinds.filter(b => b.id !== newItem.id); });
    },
    async unbind(id) {
      if (!await this.$confirm('Удалить связь?')) return;
      const bind = this.taskBinds.find(b => b.id === id);
      const { from, to, isStart } = bind;

      this.taskBinds = this.taskBinds.filter(b => b.id !== id);
      await taskBindApi.delete(id).catch(() => { this.taskBinds.push(bind); });
      this.pushCancel(() => {
        taskBindApi.create({ from, to, isStart }).then(r => {
          this.taskBinds.push(r);
        });
      });
    },
    updateTask({ id, field, value }) {
      const updatableTask = this.tasks.find(t => t.id === id);
      const prevValue = updatableTask[field];
      updatableTask[field] = value;
      taskApi.update(id, { [field]: value?.['@id'] ?? value })
        .then(() => {
          this.pushCancel(() => {
            taskApi.update(id, { [field]: prevValue?.['@id'] ?? prevValue });
            updatableTask[field] = prevValue;
          });
        })
        .catch(() => {
          this.$alert('Во время обновления произошла ошибка');
          updatableTask[field] = prevValue;
        });
    },
    loadData(setSelectedProjects = false) {
      const queryProject = +this.$route.query.project;
      if (setSelectedProjects && queryProject) {
        taskApi.fetchList({ 'stage.project': queryProject, 'stage.project.isTemplate': false, 'stage.project.isArchived': false }, true).then(r => {
          this.tasks = r.items;
          taskApi.fetchList({ 'stage.project.isTemplate': false, 'stage.project.isArchived': false }, false).then(rr => { this.tasks = rr.items; });
        });
      } else {
        taskApi.fetchList({ 'stage.project.isTemplate': false, 'stage.project.isArchived': false }, true).then(r => { this.tasks = r.items; });
      }
      stageApi.fetchList({}, true).then(r => { this.stages = r.items; });
      taskBindApi.fetchList({}, true).then(r => { this.taskBinds = r.items; });
      projectApi.fetchList({ isTemplate: false, isArchived: false }, true).then(r => {
        this.projects = r.items;

        if (!setSelectedProjects) return;
        const selectedProject = this.projects.find(p => p.id === queryProject);
        this.selectedProjects = selectedProject
          ? [selectedProject['@id']]
          : this.projects.map(p => p['@id']);
      });
    },
    setProjectsFilter(handler, preset) {
      let values;
      switch (preset) {
        case 'all':
          values = this.projects.map(p => p['@id']);
          break;
        case 'my':
          values = this.projects
            .filter(p => p.author['@id'] === this.currUser['@id'])
            .map(p => p['@id']);
          break;
        case 'none':
        default:
          values = [];
      }

      handler(values);
    },
  },
  mounted() {
    this.loadData(true);
  },
};
</script>

<template lang="pug">
  DefaultLayout(wide)
    CommonSection(title="Диаграмма Ганта")
      .gantt-page
        .filters
          BaseSelect(:options="membersOptions" v-model="filters.manager" label="Ответственный")
          BaseSelect(:options="membersOptions" v-model="filters.watcher" label="Наблюдатели")
          BaseSelect(:options="membersOptions" v-model="filters.member" label="Участвует")
          BaseSelect(:options="statusOptions" v-model="filters.status" label="Статус")
          BaseSelect(:options="priorityOptions" v-model="filters.priority" label="Приоритет")
          BaseSelectMulti(:options="projects.map(p => ({ label: p.name, value: p['@id'] }))" v-model="selectedProjects" hideNotSelected labelOuter notSelectedLabel="Проекты" useFilter style="position: relative;top: -8px;")
            template(v-slot:actions="{ setValues }")
              Btn(sm blue @click="setProjectsFilter(setValues, 'my')") Мои
              Btn(sm blue @click="setProjectsFilter(setValues, 'all')") Все
              Btn(sm grey @click="setProjectsFilter(setValues, 'none')") X
          BaseSelect.scale(:options="[{ label: 'По дням', value: 'day' }, { label: 'По неделям', value: 'week' }, { label: 'По месяцам', value: 'month' },]" v-model="scale" hideNotSelected)
          BaseCheckbox(label="Скрыть закрытые задачи" v-model="filters.hideClosed")
          Btn(@click="loadData") Обновить
        PageSlug.slug(v-if="!dataFiltered.length") Выберите проект, чтобы построить диаграмму
        template(v-else)
          .aside
            BaseSelect.sort(:options="{ dateStart: 'По дате', priority: 'По приоритету', status: 'По статусу' }" v-model="sort" label="Сортировка" hideNotSelected)
            GanttAside(
              :data="dataFiltered"
              :showStages="showStages"
              @toggleProjectFolding="toggleProjectFolding"
              @toggleStageFolding="toggleStageFolding"
              @toggleStageFoldingByProject="toggleStageFoldingByProject"
              @toggleStageVisibility="showStages = $event"
              @taskUpdate="updateTask"
            )
          .main
            GanttChart(
              v-if="projects.length && stages.length && tasks.length"
              :data="dataFiltered"
              :scale="scale"
              :binds="taskBinds"
              @update="update"
              @bind="bind"
              @unbind="unbind"
            )
</template>

<style scoped>
  .gantt-page {
    display: grid;
    grid-template-columns: 400px 1fr;
    gap: 25px;
  }

  .slug {
    grid-column: 1 / 2 span;
  }

  .sort::v-deep .box {
    height: 60px;
    background-color: #fff;
    background-position: top 24px right 15px;
  }

  .filters {
    grid-column: 1 / 2 span;
    display: flex;
    gap: 20px;
  }

  .scale {
    /*margin-left: auto;*/
  }

  .main {
    width: calc(90vw  - 400px);
  }


  @media (max-width: 1700px) {
    .main {
      width: auto;
      overflow: hidden;
    }

    .filters {
      flex-wrap: wrap;
    }
  }
</style>
